// Admin WebSocket client — mirrors client-side singleton but receives
// broadcasts for ALL conversations (the backend treats admin sockets as
// global subscribers).

import type {
  ChatWsClientFrame,
  ChatWsServerFrame,
} from '@driverforce/shared-types';

type Listener = (frame: ChatWsServerFrame) => void;

const WS_PATH = '/chat/ws';
const MAX_BACKOFF_MS = 10_000;
const PING_INTERVAL_MS = 25_000;

function resolveWsUrl(): string {
  const apiBase = import.meta.env.VITE_API_URL || '/api';
  if (/^https?:\/\//i.test(apiBase)) {
    const url = new URL(apiBase);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = `${url.pathname.replace(/\/$/, '')}${WS_PATH}`;
    return url.toString();
  }
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = `${apiBase.replace(/\/$/, '')}${WS_PATH}`;
  return `${proto}//${window.location.host}${path}`;
}

class AdminChatWs {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private listeners = new Set<Listener>();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private explicitClose = false;

  setToken(token: string | null) {
    if (this.token === token) return;
    this.token = token;
    if (!token) {
      this.disconnect();
      return;
    }
    if (this.ws) {
      this.explicitClose = false;
      try {
        this.ws.close(4000, 'token_rotated');
      } catch {
        /* */
      }
    } else {
      this.connect();
    }
  }

  onMessage(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  send(frame: ChatWsClientFrame) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(frame));
    } catch {
      /* */
    }
  }

  disconnect() {
    this.explicitClose = true;
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, 'client_disconnect');
      } catch {
        /* */
      }
      this.ws = null;
    }
  }

  private connect() {
    if (!this.token) return;
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;
    this.explicitClose = false;
    let ws: WebSocket;
    try {
      ws = new WebSocket(resolveWsUrl(), ['bearer', this.token]);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.reconnectAttempts = 0;
      if (this.pingTimer) window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        this.send({ type: 'ping' });
      }, PING_INTERVAL_MS);
    };
    ws.onmessage = (ev) => {
      try {
        const frame = JSON.parse(String(ev.data)) as ChatWsServerFrame;
        for (const fn of this.listeners) fn(frame);
      } catch {
        /* */
      }
    };
    ws.onclose = (ev) => {
      if (this.pingTimer) {
        window.clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.ws = null;
      if (ev.code === 4401 || ev.code === 4403) return;
      if (this.explicitClose) return;
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      // close handler does the work
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const attempt = ++this.reconnectAttempts;
    // Exponential backoff with ±20% jitter so a server restart doesn't
    // cause every admin tab to reconnect in lockstep.
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** (attempt - 1));
    const jitter = base * (0.8 + Math.random() * 0.4);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, jitter);
  }
}

export const adminChatWs = new AdminChatWs();
